import type { PaidReading } from '../types';
import { isCurrentPaidReading } from '../types/reading-schemas';

export interface PaidReadingPresentationField { label: string; text: string }
export interface PaidReadingPresentationSection { overview: string; details: PaidReadingPresentationField[] }
export interface PaidReadingPresentationMonth {
  month: string;
  personalMonth: number;
  theme: string;
  whyThisMonthFeelsDifferent?: string;
  opportunity: string;
  challenge: string;
  loveAndRelationships: string;
  careerAndMoney: string;
  practicalStep: string;
  reflection: string;
  affirmation: string;
}

export interface PaidReadingPresentation {
  sourceVersion: 'paid-v3' | 'legacy';
  title: string;
  preparedFor: string;
  preparedAt: string;
  personalizedSubtitle?: string;
  interpretiveQuote?: string;
  badges: string[];
  readingAtAGlance?: PaidReadingPresentationField[];
  celestialSignature: PaidReading['celestial_signature'];
  corePattern?: PaidReadingPresentationSection;
  loveAndRelationships: PaidReadingPresentationSection;
  careerAndMoney: PaidReadingPresentationSection;
  purposeAndGrowth: PaidReadingPresentationSection;
  forecast: PaidReadingPresentationMonth[];
  majorTurningPoint?: { month: string; title: string; details: PaidReadingPresentationField[] };
  sixMonthPattern?: PaidReadingPresentationSection;
  pathAhead: { title: string; message: string; closingQuote?: string };
}

export function paidForecastHorizonLabel(monthCount: number): string {
  if (monthCount === 6) return 'Six Months';
  return `${monthCount} Months`;
}

export function paidHorizonSummaryLabels(monthCount: number): [string, string, string] {
  if (monthCount === 3) return ['Month 1', 'Month 2', 'Month 3'];
  if (monthCount === 12) return ['Months 1–4', 'Months 5–8', 'Months 9–12'];
  return ['Months 1–2', 'Months 3–4', 'Months 5–6'];
}

export function toPaidReadingPresentation(reading: PaidReading): PaidReadingPresentation {
  const numerology = reading.celestial_signature.numerology;
  const forecastCount = reading.six_month_forecast.length;
  const patternLabels = paidHorizonSummaryLabels(forecastCount);
  const common = {
    title: reading.title,
    preparedFor: reading.prepared_for,
    preparedAt: reading.prepared_at,
    badges: [numerology.life_path, numerology.birthday_number, numerology.personal_year],
    celestialSignature: reading.celestial_signature,
  };
  if (isCurrentPaidReading(reading)) {
    return {
      ...common,
      sourceVersion: 'paid-v3',
      personalizedSubtitle: reading.personalized_subtitle,
      interpretiveQuote: reading.interpretive_quote,
      readingAtAGlance: [
        { label: 'Love', text: reading.reading_at_a_glance.love },
        { label: 'Career', text: reading.reading_at_a_glance.career },
        { label: 'Growth', text: reading.reading_at_a_glance.growth },
        { label: 'Major Turning Point', text: reading.reading_at_a_glance.major_turning_point },
      ],
      corePattern: { overview: reading.core_pattern.overview, details: [{ label: 'Your Strength', text: reading.core_pattern.strength }, { label: 'Your Growth Edge', text: reading.core_pattern.growth_edge }] },
      loveAndRelationships: { overview: reading.love_and_relationships.overview, details: [] },
      careerAndMoney: { overview: reading.career_and_money.overview, details: [] },
      purposeAndGrowth: { overview: reading.purpose_and_growth.overview, details: [{ label: 'Pattern to Release', text: reading.purpose_and_growth.pattern_to_release }, { label: 'Quality to Develop', text: reading.purpose_and_growth.quality_to_develop }, { label: 'Where Growth May Come From', text: reading.purpose_and_growth.where_growth_may_come_from }] },
      forecast: reading.six_month_forecast.map((month) => ({ month: month.month, personalMonth: month.personal_month, theme: month.theme, whyThisMonthFeelsDifferent: month.why_this_month_feels_different, opportunity: month.opportunity, challenge: month.challenge, loveAndRelationships: month.love_and_relationships, careerAndMoney: month.career_and_money, practicalStep: month.practical_step, reflection: month.reflection, affirmation: month.affirmation })),
      majorTurningPoint: { month: reading.major_turning_point.month, title: reading.major_turning_point.title, details: [{ label: 'Why This Period Stands Out', text: reading.major_turning_point.why_this_period_stands_out }, { label: 'What May Be Shifting', text: reading.major_turning_point.what_may_be_shifting }, { label: 'What to Pay Attention To', text: reading.major_turning_point.what_to_pay_attention_to }, { label: 'How to Work With This Energy', text: reading.major_turning_point.how_to_work_with_this_energy }] },
      sixMonthPattern: { overview: reading.six_month_pattern.overview, details: [{ label: patternLabels[0], text: reading.six_month_pattern.months_1_2 }, { label: patternLabels[1], text: reading.six_month_pattern.months_3_4 }, { label: patternLabels[2], text: reading.six_month_pattern.months_5_6 }] },
      pathAhead: { title: reading.path_ahead.title, message: reading.path_ahead.message, ...(reading.path_ahead.closing_quote.trim() ? { closingQuote: reading.path_ahead.closing_quote } : {}) },
    };
  }
  return {
    ...common,
    sourceVersion: 'legacy',
    loveAndRelationships: { overview: reading.love_and_relationships.overview, details: [{ label: 'Your Strength', text: reading.love_and_relationships.strength }, { label: 'Your Growth Edge', text: reading.love_and_relationships.growth_edge }] },
    careerAndMoney: { overview: reading.career_and_money.overview, details: [{ label: 'Your Strength', text: reading.career_and_money.strength }, { label: 'Your Growth Edge', text: reading.career_and_money.growth_edge }] },
    purposeAndGrowth: { overview: reading.purpose_and_growth.overview, details: [{ label: 'Pattern to Release', text: reading.purpose_and_growth.pattern_to_release }, { label: 'Quality to Develop', text: reading.purpose_and_growth.quality_to_develop }] },
    forecast: reading.six_month_forecast.map((month) => ({ month: month.month, personalMonth: month.personal_month, theme: month.theme, opportunity: month.opportunity, challenge: month.challenge, loveAndRelationships: month.love_focus, careerAndMoney: month.career_money_focus, practicalStep: month.practical_step, reflection: month.reflection_prompt, affirmation: month.affirmation })),
    pathAhead: { title: 'A Message for the Path Ahead', message: reading.closing_message },
  };
}
